import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tab,
  Tabs,
  useTheme,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  FilterList as FilterListIcon,
  CloudUpload as CloudUploadIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  GetApp as DownloadIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { format, isAfter, isBefore, parseISO, addDays } from 'date-fns';
import { base_url } from '../config/config';
import axios from 'axios';
import InvoiceUpload from './InvoiceUpload';
import InvoiceDetail from './InvoiceDetail';
import InvoiceForm from './InvoiceForm';

// Dashboard statistics components
const StatCard = ({ title, value, icon, color, subtitle }) => (
  <Card sx={{ height: '100%', bgcolor: color ? `${color}.50` : 'background.paper' }}>
    <CardContent>
      <Grid container spacing={2} alignItems="center">
        <Grid item>
          {icon}
        </Grid>
        <Grid item xs>
          <Typography variant="h5" component="div" fontWeight="bold">
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              {subtitle}
            </Typography>
          )}
        </Grid>
      </Grid>
    </CardContent>
  </Card>
);

const InvoiceDashboard = () => {
  const theme = useTheme();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [totalStats, setTotalStats] = useState({
    total: 0,
    paid: 0,
    unpaid: 0,
    overdue: 0,
    totalAmount: 0
  });
  
  // Detail view states
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  
  // Edit/Create form states
  const [formOpen, setFormOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  // Upload dialog state
  const [uploadOpen, setUploadOpen] = useState(false);
  
  // Action menu state
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  
  // Filter and sort states
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [sortField, setSortField] = useState('rechnungsDatum');
  const [sortDirection, setSortDirection] = useState('desc');
  const [dateFilter, setDateFilter] = useState('all');
  
  // Pagination states
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const [totalRows, setTotalRows] = useState(0);
  
  // Fetch invoices with optional filters and sorting
  const fetchInvoices = async () => {
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams({
        page: page + 1,
        limit: pageSize,
        sortField,
        sortOrder: sortDirection,
      });
      
      if (search) params.append('search', search);
      
      console.log(`Fetching invoices with params: ${params.toString()}`);
      const response = await axios.get(`${base_url}/v1/getInvoices?${params}`);
      
      console.log('API Response:', response.data);
      
      if (response.data.status === 1) {
        // Check if response data is valid
        if (!Array.isArray(response.data.data)) {
          console.error('Invalid response data format:', response.data.data);
          setSnackbar({
            open: true,
            message: 'Fehler: Ungültiges Datenformat vom Server',
            severity: 'error'
          });
          setInvoices([]);
          setTotalRows(0);
          setLoading(false);
          return;
        }
        
        console.log(`Received ${response.data.data.length} invoices from API`);
        
        // Log the first invoice for debugging
        if (response.data.data.length > 0) {
          console.log('Sample invoice:', {
            _id: response.data.data[0]._id,
            rechnungsNummer: response.data.data[0].rechnungsNummer,
            gesamtbetrag: response.data.data[0].gesamtbetrag,
            rechnungsDatum: response.data.data[0].rechnungsDatum,
            faellig: response.data.data[0].faellig
          });
        }
        
        setInvoices(response.data.data);
        setTotalRows(response.data.totalInvoices);
        
        // Calculate statistics
        const stats = {
          total: response.data.totalInvoices,
          paid: 0,
          unpaid: 0,
          overdue: 0,
          totalAmount: 0
        };
        
        response.data.data.forEach(invoice => {
          // Parse gesamtbetrag as a number
          const amount = parseFloat(invoice.gesamtbetrag) || 0;
          stats.totalAmount += amount;
          
          if (invoice.bezahltAm) {
            stats.paid++;
          } else {
            stats.unpaid++;
            
            // Check if overdue - handle date parsing safely
            try {
              if (invoice.faellig) {
                const dueDate = typeof invoice.faellig === 'string' ? parseISO(invoice.faellig) : new Date(invoice.faellig);
                if (isAfter(new Date(), dueDate)) {
                  stats.overdue++;
                }
              }
            } catch (e) {
              console.error('Error parsing due date:', invoice.faellig, e);
            }
          }
        });
        
        console.log('Calculated statistics:', stats);
        setTotalStats(stats);
      } else {
        console.error('Error status in response:', response.data);
        setSnackbar({
          open: true,
          message: response.data.message || 'Fehler beim Laden der Rechnungen',
          severity: 'error'
        });
        setInvoices([]);
        setTotalRows(0);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setSnackbar({
        open: true,
        message: 'Fehler beim Laden der Rechnungen',
        severity: 'error'
      });
      setInvoices([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };
  
  // Initial fetch
  useEffect(() => {
    fetchInvoices();
  }, [page, pageSize, sortField, sortDirection, tabValue, search]);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Reset pagination
    setPage(0);
  };
  
  // Filter invoices based on tab
  const filteredInvoices = invoices.filter(invoice => {
    const today = new Date();
    
    switch(tabValue) {
      case 0: // All
        return true;
      case 1: // Paid
        return !!invoice.bezahltAm;
      case 2: // Unpaid
        return !invoice.bezahltAm;
      case 3: // Overdue
        return !invoice.bezahltAm && 
               invoice.faellig && 
               isAfter(today, parseISO(invoice.faellig));
      case 4: // Due soon
        return !invoice.bezahltAm && 
               invoice.faellig && 
               isAfter(parseISO(invoice.faellig), today) && 
               isBefore(parseISO(invoice.faellig), addDays(today, 7));
      default:
        return true;
    }
  });
  
  // Handle row click to open detail view
  const handleRowClick = (params) => {
    setSelectedInvoice(params.row);
    setDetailOpen(true);
  };
  
  // Handle opening action menu
  const handleActionClick = (event, params) => {
    event.stopPropagation();
    setSelectedRow(params.row);
    setActionMenuAnchor(event.currentTarget);
  };
  
  // Handle closing action menu
  const handleActionClose = () => {
    setActionMenuAnchor(null);
  };
  
  // Handle edit action
  const handleEdit = () => {
    setSelectedInvoice(selectedRow);
    setEditMode(true);
    setFormOpen(true);
    handleActionClose();
  };
  
  // Handle delete action
  const handleDelete = async () => {
    if (!selectedRow) return;
    
    if (window.confirm('Sind Sie sicher, dass Sie diese Rechnung löschen möchten?')) {
      try {
        const response = await axios.delete(`${base_url}/v1/deleteInvoice/${selectedRow._id}`);
        
        if (response.data.status === 1) {
          setSnackbar({
            open: true,
            message: 'Rechnung erfolgreich gelöscht',
            severity: 'success'
          });
          fetchInvoices();
        }
      } catch (error) {
        console.error('Error deleting invoice:', error);
        setSnackbar({
          open: true,
          message: 'Fehler beim Löschen der Rechnung',
          severity: 'error'
        });
      }
    }
    handleActionClose();
  };
  
  // Handle create action
  const handleCreate = () => {
    setSelectedInvoice(null);
    setEditMode(false);
    setFormOpen(true);
  };
  
  // Handle form submit
  const handleFormSubmit = async (invoiceData) => {
    try {
      let response;
      
      if (editMode) {
        response = await axios.put(`${base_url}/v1/updateInvoice/${selectedInvoice._id}`, invoiceData);
      } else {
        response = await axios.post(`${base_url}/v1/createInvoice`, invoiceData);
      }
      
      if (response.data.status === 1) {
        setSnackbar({
          open: true,
          message: editMode ? 'Rechnung erfolgreich aktualisiert' : 'Rechnung erfolgreich erstellt',
          severity: 'success'
        });
        setFormOpen(false);
        fetchInvoices();
      }
    } catch (error) {
      console.error('Error saving invoice:', error);
      setSnackbar({
        open: true,
        message: 'Fehler beim Speichern der Rechnung',
        severity: 'error'
      });
    }
  };
  
  // Handle upload success
  const handleUploadSuccess = (data) => {
    setSnackbar({
      open: true,
      message: `${data.invoicesCreated} Rechnungen erfolgreich hochgeladen. ${data.customersCreated} Kunden erstellt/aktualisiert.`,
      severity: 'success'
    });
    setUploadOpen(false);
    fetchInvoices();
  };
  
  // DataGrid columns definition
  const columns = [
    { 
      field: 'rechnungsNummer', 
      headerName: 'RG-Nr.', 
      flex: 1.2,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium" color="primary">
          {params.value || '-'}
        </Typography>
      )
    },
    { 
      field: 'firmenname', 
      headerName: 'Firma', 
      flex: 2,
      valueGetter: (params) => {
        if (params.row.kundenID && params.row.kundenID.bezeichnung) {
          return params.row.kundenID.bezeichnung;
        }
        return params.row.firmenname || '-';
      }
    },
    { 
      field: 'rechnungsDatum', 
      headerName: 'Datum', 
      flex: 1,
      valueFormatter: (params) => {
        if (!params.value) return '-';
        try {
          return format(new Date(params.value), 'dd.MM.yyyy');
        } catch (error) {
          console.error('Error formatting date:', params.value);
          return params.value || '-';
        }
      }
    },
    { 
      field: 'gesamtbetrag', 
      headerName: 'Betrag', 
      flex: 1,
      type: 'number',
      renderCell: (params) => {
        let value = params.value;
        if (value === null || value === undefined) return '-';
        
        // If the value is a string, try to parse it
        if (typeof value === 'string') {
          try {
            value = parseFloat(value);
          } catch (e) {
            console.error('Error parsing amount:', value);
            return value;
          }
        }
        
        // Handle NaN
        if (isNaN(value)) return '-';
        
        return (
          <Typography variant="body2" fontWeight="medium">
            {`${value.toFixed(2)} €`}
          </Typography>
        );
      }
    },
    { 
      field: 'faellig', 
      headerName: 'Fällig', 
      flex: 1,
      valueFormatter: (params) => {
        if (!params.value) return '-';
        try {
          return format(new Date(params.value), 'dd.MM.yyyy');
        } catch (error) {
          console.error('Error formatting due date:', params.value);
          return params.value || '-';
        }
      },
      renderCell: (params) => {
        if (!params.value) return '-';
        
        let dueDate;
        try {
          dueDate = typeof params.value === 'string' ? parseISO(params.value) : new Date(params.value);
          if (isNaN(dueDate.getTime())) throw new Error('Invalid date');
        } catch (error) {
          console.error('Error parsing due date in renderCell:', params.value);
          return params.value || '-';
        }
        
        const today = new Date();
        
        let color = 'success';
        if (!params.row.bezahltAm) {
          if (isAfter(today, dueDate)) {
            color = 'error';
          } else if (isAfter(dueDate, today) && isBefore(dueDate, addDays(today, 7))) {
            color = 'warning';
          }
        }
        
        return (
          <Chip 
            size="small" 
            label={format(dueDate, 'dd.MM.yyyy')}
            color={color} 
            variant={color === 'success' ? 'outlined' : 'filled'} 
          />
        );
      }
    },
    { 
      field: 'bezahltAm', 
      headerName: 'Status', 
      flex: 1,
      renderCell: (params) => (
        params.value ? 
          <Chip size="small" icon={<PaymentIcon />} label="Bezahlt" color="success" /> : 
          <Chip size="small" icon={<ReceiptIcon />} label="Offen" color="default" variant="outlined" />
      )
    },
    { 
      field: 'actions', 
      headerName: 'Aktionen', 
      flex: 0.8,
      sortable: false,
      renderCell: (params) => (
        <IconButton 
          onClick={(event) => handleActionClick(event, params)}
          aria-label="show actions"
        >
          <MoreVertIcon />
        </IconButton>
      )
    },
  ];
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Grid container spacing={3} alignItems="center" sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="h4" component="h1" gutterBottom>
            Rechnungsverwaltung
          </Typography>
        </Grid>
        <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            Neue Rechnung
          </Button>
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={() => setUploadOpen(true)}
          >
            Excel importieren
          </Button>
        </Grid>
      </Grid>
      
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Offene Rechnungen"
            value={totalStats.unpaid}
            icon={<ReceiptIcon sx={{ fontSize: 40, color: 'primary.main' }} />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Überfällige Rechnungen"
            value={totalStats.overdue}
            icon={<PaymentIcon sx={{ fontSize: 40, color: 'error.main' }} />}
            color="error"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Bezahlte Rechnungen"
            value={totalStats.paid}
            icon={<PaymentIcon sx={{ fontSize: 40, color: 'success.main' }} />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Gesamtbetrag"
            value={`${totalStats.totalAmount.toFixed(2)} €`}
            icon={<ReceiptIcon sx={{ fontSize: 40, color: 'info.main' }} />}
            color="info"
          />
        </Grid>
      </Grid>
      
      {/* Filters and search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Suche nach Rechnung, Kunde oder Kommentar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton 
                      size="small" 
                      onClick={() => setSearch('')}
                      edge="end"
                    >
                      <RefreshIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              startIcon={<FilterListIcon />}
              variant="outlined"
              onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
            >
              Filter & Sortierung
            </Button>
            
            {/* Filter menu */}
            <Menu
              anchorEl={filterMenuAnchor}
              open={Boolean(filterMenuAnchor)}
              onClose={() => setFilterMenuAnchor(null)}
            >
              <MenuItem disabled>
                <Typography variant="subtitle2">Sortieren nach</Typography>
              </MenuItem>
              <MenuItem 
                onClick={() => {
                  setSortField('rechnungsDatum');
                  setSortDirection('desc');
                  setFilterMenuAnchor(null);
                }}
                selected={sortField === 'rechnungsDatum' && sortDirection === 'desc'}
              >
                <ArrowDownwardIcon fontSize="small" sx={{ mr: 1 }} />
                Neueste zuerst
              </MenuItem>
              <MenuItem 
                onClick={() => {
                  setSortField('rechnungsDatum');
                  setSortDirection('asc');
                  setFilterMenuAnchor(null);
                }}
                selected={sortField === 'rechnungsDatum' && sortDirection === 'asc'}
              >
                <ArrowUpwardIcon fontSize="small" sx={{ mr: 1 }} />
                Älteste zuerst
              </MenuItem>
              <MenuItem 
                onClick={() => {
                  setSortField('gesamtbetrag');
                  setSortDirection('desc');
                  setFilterMenuAnchor(null);
                }}
                selected={sortField === 'gesamtbetrag' && sortDirection === 'desc'}
              >
                <ArrowDownwardIcon fontSize="small" sx={{ mr: 1 }} />
                Höchster Betrag
              </MenuItem>
              <MenuItem 
                onClick={() => {
                  setSortField('gesamtbetrag');
                  setSortDirection('asc');
                  setFilterMenuAnchor(null);
                }}
                selected={sortField === 'gesamtbetrag' && sortDirection === 'asc'}
              >
                <ArrowUpwardIcon fontSize="small" sx={{ mr: 1 }} />
                Niedrigster Betrag
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => {
                setFilterMenuAnchor(null);
                fetchInvoices();
              }}>
                <RefreshIcon fontSize="small" sx={{ mr: 1 }} />
                Filter zurücksetzen
              </MenuItem>
            </Menu>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Main content */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={`Alle (${totalStats.total})`} />
          <Tab label={`Bezahlt (${totalStats.paid})`} />
          <Tab label={`Offen (${totalStats.unpaid})`} />
          <Tab label={`Überfällig (${totalStats.overdue})`} icon={<Chip size="small" color="error" label={totalStats.overdue} />} iconPosition="end" />
          <Tab label="Bald fällig" />
        </Tabs>
        
        <Box sx={{ height: 600, width: '100%' }}>
          {invoices.length === 0 && !loading ? (
            <Paper sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography variant="h6" gutterBottom>Keine Rechnungen gefunden</Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                Es wurden keine Rechnungen gefunden. Bitte importieren Sie Rechnungen oder erstellen Sie neue Rechnungen.
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<CloudUploadIcon />}
                onClick={() => setUploadOpen(true)}
                sx={{ mt: 3 }}
              >
                Excel importieren
              </Button>
            </Paper>
          ) : (
            <DataGrid
              rows={filteredInvoices || []}
              columns={columns}
              getRowId={(row) => row._id || Math.random().toString()}
              loading={loading}
              pageSizeOptions={[10, 15, 25, 50]}
              pagination
              paginationMode="server"
              rowCount={totalRows}
              page={page}
              pageSize={pageSize}
              onPageChange={(newPage) => setPage(newPage)}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              disableRowSelectionOnClick
              onRowClick={handleRowClick}
              autoHeight
              sx={{
                '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus': {
                  outline: 'none',
                }
              }}
              initialState={{
                pagination: { paginationModel: { pageSize } },
              }}
              error={null}
            />
          )}
        </Box>
      </Paper>
      
      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionClose}
      >
        <MenuItem onClick={() => {
          setSelectedInvoice(selectedRow);
          setDetailOpen(true);
          handleActionClose();
        }}>
          <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
          Details anzeigen
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Bearbeiten
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Löschen
        </MenuItem>
      </Menu>
      
      {/* Invoice Detail Dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedInvoice && (
          <InvoiceDetail 
            invoice={selectedInvoice} 
            onClose={() => setDetailOpen(false)}
            onEdit={() => {
              setDetailOpen(false);
              setEditMode(true);
              setFormOpen(true);
            }}
          />
        )}
      </Dialog>
      
      {/* Invoice Create/Edit Form */}
      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <InvoiceForm 
          invoice={selectedInvoice}
          isEdit={editMode}
          onClose={() => setFormOpen(false)}
          onSubmit={handleFormSubmit}
        />
      </Dialog>
      
      {/* Excel Upload Dialog */}
      <Dialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <InvoiceUpload 
          onClose={() => setUploadOpen(false)}
          onSuccess={handleUploadSuccess}
        />
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InvoiceDashboard;